import { useContext } from "react";
import { makeStyles } from "@mui/styles";
import { DbUser } from "../../../../interfaces";
import {
  DataTablePaginationProps,
  DataTableSortProps,
} from "../../../../pages/v2/UsersPage";
import { DataGrid, GridColDef, GridColumns, GridValidRowModel } from "@mui/x-data-grid";
import { ThemeContext } from "../../../../context/ThemeContext";
import { ThemeCtx } from "../../../../interfaces";
import { AlertState } from "../../../../types";

const useStyles = makeStyles((theme) => ({
  root: {
    textAlign: "left",
    display: "flex",
    flexDirection: "column",
  },
  dataTable: {
    borderRadius: "8px",
    backgroundColor: "white",
    borderColor: "#E8E8E8",
    "& .MuiDataGrid-iconButtonContainer": {
      marginLeft: "auto",
    },
    "& .MuiDataGrid-columnHeaders": {
      height: "50px",
      minHeight: "unset",
    },
    "& .MuiDataGrid-columnHeader": {
      "&:focus, &:focus-within": {
        outline: "none",
      },
    },
    "& .MuiDataGrid-columnHeader--sorted": {
      color: theme.palette.primary.main,
      "& .MuiDataGrid-sortIcon": {
        color: theme.palette.primary.main,
      },
    },
    "& .MuiDataGrid-cell": {
      border: "0px",
      "&:focus, &:focus-within": {
        outline: "none",
      },
    },
  },
  dataTableHeader: {
    "& > .MuiDataGrid-columnSeparator": {
      visibility: "hidden",
    },
  },
  classesTable: {
    "& thead": {
      color: "#FFF",
      backgroundColor: theme.palette.primary.main,
    },
    "& th": {
      padding: "0.625rem",
    },
    "& td": {
      padding: "0.625rem",
    },
  },
  evenRow: {
    backgroundColor: "#FFF",
  },
  oddRow: {
    backgroundColor: "#F5F5F5",
  },
  deleteUserButton: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main,
    color: "#FFFFFF",
    fontSize: "1rem",
    fontWeight: 600,
    borderRadius: "2.25rem",
    paddingLeft: "1rem",
    paddingRight: "1rem",
    paddingTop: "0.5rem",
    paddingBottom: "0.5rem",
    borderStyle: "solid",
    borderWidth: "1px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",

    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

interface GenericDataTableProps<T extends GridValidRowModel> {
  currentUser: DbUser;
  pagination?: DataTablePaginationProps;
  sort: DataTableSortProps;
  data: T[];
  columnNames: (keyof T)[];
  fieldMap: Map<keyof T, GridColDef<T>>;
  onDelete: (alert: AlertState | null) => void;
}

export function GenericDataTable<T extends GridValidRowModel>(
  props: GenericDataTableProps<T>
) {
  const classes = useStyles();
  const themeCtx = useContext(ThemeContext) as ThemeCtx;

  function getField(fieldName: keyof T): GridColDef<T> | void {
    const field = props.fieldMap.get(fieldName as keyof T);
    return field;
  }

  const renderCell = (params: any) => {
    return (
      <div
        style={{
          fontWeight: props.sort?.field === params.field ? 500 : "inherit",
          color:
            props.sort?.field === params.field
              ? themeCtx.theme.palette.primary.main
              : "inherit",
        }}>
        {params.value}
      </div>
    );
  };

  const buildColumnDef = (): GridColumns<T> => {
    const columns: GridColumns<T> = [];
    props.columnNames.forEach((fieldName) => {
      const newColumn = getField(fieldName)!;
      newColumn.headerClassName = classes.dataTableHeader;
      if (!newColumn.renderCell) newColumn.renderCell = renderCell;
      columns.push(newColumn);
    });

    return columns;
  };

  return (
    <>
      <div className={classes.root}>
        <div style={{ width: "100%" }}>
          <DataGrid
            className={classes.dataTable}
            rows={props.data}
            columns={buildColumnDef()}
            sortingMode="server"
            onSortModelChange={props.sort.handleSort}
            initialState={{
              sorting: {
                sortModel: [
                  {
                    field: props.sort.field,
                    sort: props.sort.sort,
                  },
                ],
              },
            }}
            autoHeight
            disableSelectionOnClick
            disableColumnSelector
            disableDensitySelector
            disableColumnMenu
            hideFooter
          />
        </div>
      </div>
    </>
  );
}
